import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { ClockArrowDown } from "lucide-react";

import messages from "../../../assets/locale/messages";
import Filters from "../../Filter";
import DateRangeFilterInput from "../../Filter/DateRangeFilterInput";
import ActivityLogTable from "../ActivityLogTable";
import ActivityLogExportModal from "../ActivityLogExportModal";
import { UIButton, UIEmptyState, UIPaginationBar } from "../../ui";
import {
  COMPANY_ACTIVITY_MODULES,
  COMPANY_ACTIVITY_MODULE_BY_API_KEY,
  formatActivityDateTime,
} from "../../Dashboard/RecentActivityLog/constants";
import useDebounce from "../../../hooks/useDebounce";
import "../ActivityLog.scss";

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 450;

const API_KEY_BY_MODULE = Object.fromEntries(
  Object.entries(COMPANY_ACTIVITY_MODULE_BY_API_KEY).map(([key, id]) => [
    id,
    key,
  ]),
);

/**
 * One company's activity log — shared by the company portal's "Activity Log"
 * page and the Super Admin company profile's "Logs" tab (same columns, rows,
 * filters and pagination). Newest first, no Company / Event column: search by
 * actor name, filter by module (multi), date range and — when `roleOptions` is
 * given — role (multi), paginate, and export the matching entries. Entries are
 * read-only.
 * Server-driven: the view owns the search / filter / paging state and reports
 * it as PROLINE-3925 query params through `onQueryChange`; the parent fetches
 * and passes back one page of `rows` with the `count` / `pages` totals.
 */
const CompanyActivityLogView = ({
  rows,
  count,
  pages,
  loading,
  roleOptions,
  onQueryChange,
  loadExportEntries,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { activityLog: copy, dashboard, shared } = messages[lang];
  const moduleLabels = dashboard.recentActivity.modules;

  const [search, setSearch] = useState("");
  const [moduleFilter, setModuleFilter] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [roleFilter, setRoleFilter] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showExportModal, setShowExportModal] = useState(false);

  const debouncedSearch = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS);

  const moduleOptions = useMemo(
    () =>
      COMPANY_ACTIVITY_MODULES.map((id) => ({ id, name: moduleLabels[id] })),
    [moduleLabels],
  );

  // The current search + filters as API params (no paging).
  const filterParams = useMemo(() => {
    const params = {};
    if (debouncedSearch) params.company_user_name = debouncedSearch;
    if (moduleFilter.length) {
      params.module = moduleFilter
        .map((option) => API_KEY_BY_MODULE[option.id])
        .join(",");
    }
    if (roleFilter.length) {
      params.company_role_id = roleFilter.map((option) => option.id).join(",");
    }
    if (dateRange?.startDate) {
      params.created_at_start_date = dateRange.startDate;
    }
    if (dateRange?.endDate) params.created_at_end_date = dateRange.endDate;
    return params;
  }, [dateRange, debouncedSearch, moduleFilter, roleFilter]);

  useEffect(() => {
    onQueryChange({ ...filterParams, page, items: pageSize });
  }, [filterParams, onQueryChange, page, pageSize]);

  // Entries matching the search + filters (across all pages).
  const matchingCount = count;
  const rangeFrom = matchingCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, matchingCount);

  const hasActiveFilters = Boolean(
    search.trim() || moduleFilter.length || dateRange || roleFilter.length,
  );
  // Nothing logged yet → nothing to search, filter or export: hide the bar.
  // It stays while a search / filter is active, so it can be cleared.
  const showFilters = hasActiveFilters || matchingCount > 0;

  /** Wraps a filter setter so any change goes back to page 1. */
  const onFilterChange = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setModuleFilter([]);
    setDateRange(null);
    setRoleFilter([]);
    setPage(1);
  };

  // Export modal fields — the table's columns, with the actor split into name
  // and role. `value` reads the cell for one entry.
  const exportFields = useMemo(() => {
    const labels = copy.exportFields;
    return [
      {
        name: "time",
        label: labels.time,
        value: (entry) => formatActivityDateTime(entry.createdAt, lang),
      },
      {
        name: "actorName",
        label: labels.actorName,
        value: (entry) => entry.actor.name || "—",
      },
      {
        name: "actorRole",
        label: labels.actorRole,
        value: (entry) => entry.actor.role || "—",
      },
      { name: "action", label: labels.action, value: (entry) => entry.action },
      {
        name: "module",
        label: labels.module,
        value: (entry) => moduleLabels[entry.module] || "—",
      },
    ];
  }, [copy.exportFields, lang, moduleLabels]);

  return (
    <div className="activity-log__view">
      {showFilters && (
        <Filters
          isCollapsable
          searchInput={{
            placeholder: copy.searchPlaceholder,
            name: "companyActivityLogSearch",
            value: search,
            onChange: onFilterChange(setSearch),
          }}
          renderActionBeforeFilterToggle={
            <UIButton
              type="button"
              variant="primary"
              appearance="outline"
              size="large"
              disabled={matchingCount === 0}
              onClick={() => setShowExportModal(true)}
            >
              {copy.exportAll}
            </UIButton>
          }
          filters={[
            {
              type: "filter",
              name: "companyActivityLogModule",
              placeholder: copy.filters.module,
              value: moduleFilter,
              options: moduleOptions,
              isMultiple: true,
              onChange: onFilterChange((value) => setModuleFilter(value || [])),
            },
            {
              type: "component",
              name: "companyActivityLogDateRange",
              render: () => (
                <DateRangeFilterInput
                  name="companyActivityLogDateRange"
                  placeholder={copy.filters.dateRange}
                  value={dateRange}
                  onChange={onFilterChange(setDateRange)}
                />
              ),
            },
            ...(roleOptions
              ? [
                  {
                    type: "filter",
                    name: "companyActivityLogRole",
                    placeholder: copy.company.filters.role,
                    value: roleFilter,
                    options: roleOptions,
                    isMultiple: true,
                    onChange: onFilterChange((value) =>
                      setRoleFilter(value || []),
                    ),
                  },
                ]
              : []),
          ]}
          classNames={{
            container: "activity-log__filters",
            base: "",
            search: "mb-3 mb-md-0",
            filter: "",
          }}
        />
      )}

      {matchingCount === 0 ? (
        // Nothing logged yet vs. nothing matching the search / filters.
        !loading && (
          <UIEmptyState
            title={hasActiveFilters ? copy.empty.title : copy.noActivity.title}
            description={
              hasActiveFilters
                ? copy.empty.description
                : copy.company.noActivityDescription
            }
            icon={<ClockArrowDown aria-hidden />}
            actionLabel={hasActiveFilters ? copy.empty.clear : null}
            onAction={hasActiveFilters ? clearFilters : undefined}
          />
        )
      ) : (
        <>
          <ActivityLogTable
            rows={rows}
            labels={copy}
            modules={moduleLabels}
            lang={lang}
            showCompany={false}
          />
          <UIPaginationBar
            currentPage={page}
            pages={pages}
            pageSize={pageSize}
            rangeTemplate={copy.showingRange}
            rangeFrom={rangeFrom}
            rangeTo={rangeTo}
            rangeTotal={matchingCount}
            rowsPerPageLabel={shared.numberOfRows}
            onPageChange={setPage}
            onPageSizeChange={(nextSize) => {
              setPageSize(nextSize);
              setPage(1);
            }}
            className="activity-log__pagination"
          />
        </>
      )}

      <ActivityLogExportModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        fields={exportFields}
        loadEntries={() => loadExportEntries(filterParams)}
        copy={copy}
        cancelLabel={shared.cancel}
      />
    </div>
  );
};

CompanyActivityLogView.propTypes = {
  /** One page of `toActivityLogRow` rows. */
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      createdAt: PropTypes.string.isRequired,
      actor: PropTypes.shape({
        name: PropTypes.string.isRequired,
        role: PropTypes.string,
      }).isRequired,
      module: PropTypes.string,
      action: PropTypes.string.isRequired,
    }),
  ).isRequired,
  /** Entries matching the search + filters, across all pages. */
  count: PropTypes.number.isRequired,
  pages: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  /**
   * Role filter options (`{ id, name }`); `id` is sent as `company_role_id`.
   * Leave it out to hide the Role filter.
   */
  roleOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  /** Called with the query params (filters + `page` / `items`) on every change. Keep it stable. */
  onQueryChange: PropTypes.func.isRequired,
  /** Async — every entry matching `filterParams` as rows, or `null` when the request failed. */
  loadExportEntries: PropTypes.func.isRequired,
};

CompanyActivityLogView.defaultProps = {
  loading: false,
  roleOptions: null,
};

export default CompanyActivityLogView;
